import React, { useEffect, useState } from "react";
import { Box, Divider, List, ListItemButton } from "@mui/material";
import { ISessionState } from "../../../models/session/sessionState";
import * as documentService from "../../../models/services/documentService";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { IFolderTreeNode } from "source/models/document/folderTreeNode";
import { DocumentTreeFolderNode } from "./DocumentFolderTreeNode";
import { PageTitle } from "source/components/framework/Page/PageTitle";
import { AddButton } from "source/components/framework/Button/AddButton";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { LocalLoading } from "source/components/framework/Loading/LocalLoading";
import { DocumentList } from "../DocumentList";
import { ConfirmationDialogResult, Permission } from "ui/Scripts/source/models/enums";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { DocumentCopyMoveDialog } from "../DocumentCopyMoveDialog";
import { DocumentFolderNewDialog } from "../DocumentFolderNewDialog";
import { DocumentFolderEditDialog } from "../DocumentFolderEditDialog";
import { IDocumentPageFilterState } from "../DocumentPageDashboardChart";
import { CompanyItem } from "source/components/company/Company/CompanyItem";

interface IDocumentFoldersProps extends IDocumentPageFilterState {
    sessionState: ISessionState;
    canEditProperties: boolean;
    companies?: ICompanyMenuItem[];
    companyId: string;
    dataId: string;
    listFilterIsSet: boolean;
    showDocumentPreview: boolean;
    showPublishedVersionOnDocumentPreview: boolean;
    editMode: boolean;
    settingsVisible: boolean;
    onShowSettingsDialog: () => void;
    onChangeShowDocumentPreview?: (showDocumentPreview: boolean) => void;
    onChangeShowPublishedVersionOnDocumentPreview?: (showPublishedVersionOnDocumentPreview: boolean) => void;
    onChangeEditMode?: (editMode: boolean) => void;
    onOpenFiltersPanel: () => void;
    onChangeActiveCompany?: (companyId: string) => void;
}

interface IDocumentFolderTreeState {
    companyId: string;
    isLoading: boolean;
    folderTree: IFolderTreeNode;
    folderTreeNodes: IFolderTreeNode[];
    selectedId: string;
    permission: Permission;
    folderComment: string;
}

interface IDocumentListState {
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

interface INewFolderDialogState {
    show: boolean;
    folder: IFolderTreeNode;
}

interface IEditFolderDialogState {
    show: boolean;
    folder: IFolderTreeNode;
}

export const DocumentFolders = (props: IDocumentFoldersProps) => {
    const [treeState, setTreeState] = useState<IDocumentFolderTreeState>({ companyId: "", isLoading: true, folderTree: null, folderTreeNodes: [], selectedId: "", permission: Permission.None, folderComment: "" });
    const [listState, setListState] = useState<IDocumentListState>({ filter: "", sortColumn: "name", sortOrderIsAsc: true });
    const [showCopyMoveDialog, setShowCopyMoveDialog] = useState(false);
    const [newFolderDialogState, setNewFolderDialogState] = useState<INewFolderDialogState>({ show: false, folder: null });
    const [editFolderDialogState, setEditFolderDialogState] = useState<IEditFolderDialogState>({ show: false, folder: null });

    const loadFolderNode = async (newSelectedId: string) => {
        setTreeState(prevState => ({
            ...prevState,
            ...{
                isLoading: true
            }
        }));
        if (!props.companyId) return;
        await AppUtils.callService(() => documentService.getDocumentFolders(props.companyId), false, true, (result) => {
            if (result) {
                const folderTreeNodes = result.folderTree.getAsList([]);
                let selectedId = result.folderTree.id;
                let selectedFolderNode = result.folderTree;
                if (newSelectedId) {
                    selectedFolderNode = folderTreeNodes.find(i => i.id === newSelectedId);
                    if (selectedFolderNode) {
                        selectedId = newSelectedId;
                        selectedFolderNode.expandToRoot();
                    }
                }
                setTreeState({
                    companyId: props.companyId,
                    isLoading: false,
                    folderTree: result.folderTree,
                    folderTreeNodes,
                    selectedId,
                    permission: selectedFolderNode.permission,
                    folderComment: selectedFolderNode.name + (selectedFolderNode.comment ? ": " + selectedFolderNode.comment : "")
                });
            } else {
                setTreeState(prevState => ({
                    ...prevState,
                    ...{
                        isLoading: false
                    }
                }));
            }
        });
    };

    useEffect(() => {
        loadFolderNode(null);
    }, [props.companyId, props.dataId]);

    const handleClick = (aNode: IFolderTreeNode) => {
        if (!aNode?.id) return;
        const folderTreeNodes = treeState.folderTreeNodes.slice(0);
        const node = folderTreeNodes.find(i => i.id === aNode.id);
        if (!node) return;
        if (node.parent) {
            node.expanded = !node.expanded;
        }
        setTreeState(prevState => ({
            ...prevState,
            ...{
                folderTreeNodes,
                selectedId: aNode.id,
                permission: node.permission,
                folderComment: node.name + (node.comment ? ": " + node.comment : "")
            }
        }));
    };

    // #region Add Folder
    const handleAddFolder = () => {
        if (!treeState.selectedId) return;
        const folder = treeState.folderTreeNodes.find(i => i.id === treeState.selectedId);
        if (!folder) return;
        setNewFolderDialogState({
            show: true,
            folder
        });
    };

    const handleDocumentFolderNewDialogOk = (newFolderId: string) => {
        setNewFolderDialogState({
            show: false,
            folder: null
        });
        loadFolderNode(newFolderId);
    };

    const handleDocumentFolderNewDialogCancel = () => {
        setNewFolderDialogState({
            show: false,
            folder: null
        });
    };
    // #endregion Add Folder

    // #region Edit Folder
    const handleEditFolder = () => {
        if (!treeState.selectedId) return;
        const folder = treeState.folderTreeNodes.find(i => i.id === treeState.selectedId);
        if (!folder) return;
        setEditFolderDialogState({
            show: true,
            folder
        });
    };

    const getSelectedId = (newSelectedId: string) => {
        let result = newSelectedId;
        if (!result && editFolderDialogState.folder.parent) {
            const index = editFolderDialogState.folder.parent.children.findIndex(i => i.id === editFolderDialogState.folder.id);
            result = index > 0
                ? editFolderDialogState.folder.parent.children[index - 1].id
                : editFolderDialogState.folder.parent.id;
        }
        return result;
    };

    const handleDocumentFolderEditDialogOk = (newSelectedId: string) => {
        loadFolderNode(getSelectedId(newSelectedId));
    };

    const handleDocumentFolderEditDialogCancel = (newSelectedId: string) => {
        setEditFolderDialogState({
            show: false,
            folder: null
        });
        loadFolderNode(getSelectedId(newSelectedId));
    };
    // #endregion Edit Folder

    const handleDeleteFolder = async (id: string) => {
        if (!id) return;
        const folderTreeNode = treeState.folderTreeNodes.find(i => i.id === id);
        if (!folderTreeNode || !folderTreeNode.parent) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveFolderParameter, folderTreeNode.name), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await AppUtils.callService(() => documentService.removeFolder(props.companyId, id), true);
            if (!result) return;
            loadFolderNode(folderTreeNode.parent.id);
        }
    };

    // #region Copy / Move
    const handleCopyFolder = () => {
        setShowCopyMoveDialog(true);
    };

    const handleCopyMoveDialogOk = (refreshFolders: boolean, selectedId: string) => {
        setShowCopyMoveDialog(false);
        if (!refreshFolders) return;
        loadFolderNode(selectedId);
    };
    
    const handleCopyMoveDialogCancel = () => {
        setShowCopyMoveDialog(false);
    };
    // #endregion Copy / Move

    const handleSetSortData = (sortColumn: string, sortOrderIsAsc: boolean) => {
        setListState(prevState => ({
            ...prevState,
            ...{ sortColumn, sortOrderIsAsc }
        }));
    };

    const handleChangeFilter = (filter: string) => {
        setListState(prevState => ({
            ...prevState,
            ...{ filter }
        }));
    };

    const isSuperAdmin = props.sessionState.isSuperAdmin;

    const showCompanies = !isSuperAdmin && !treeState.isLoading && props.companies?.length > 1;

    const company = showCompanies
        ? props.companies.find(i => i.id === props.companyId)
        : null;

    return (
        <Box display="flex">
            <div style={{ minWidth: "350px", maxWidth: "350px", position: "relative", flexBasis: 0, flexGrow: 2, display: "flex" }}>
                <PageTitle title={Translations.Folders} position="absolute" padding={"24px 0 24px 24px"}>
                    {props.editMode && treeState.permission > Permission.Read &&
                        <AddButton onClick={handleAddFolder} sx={{ margingLeft: "24px" }}>
                            {Translations.AddFolder}
                        </AddButton>
                    }
                </PageTitle>
                <MainListContainer sx={{ width: "100%", height: "calc(100vh - 160px)", overflow: "auto", marginRight: "0", position: "relative" }}>
                    {!!company &&
                        <Box>
                            <ListItemButton>
                                <CompanyItem company={company} />
                            </ListItemButton>
                            <Divider />
                        </Box>
                    }
                    <List>
                        {treeState.folderTreeNodes.map((node, index) =>
                            <DocumentTreeFolderNode
                                key={node.id}
                                sessionState={props.sessionState}
                                companyId={props.companyId}
                                editMode={props.editMode}
                                node={node}
                                selectedId={treeState.selectedId}
                                isFirstNode={index < 1}
                                onClick={handleClick}
                                onEdit={handleEditFolder}
                                onDelete={handleDeleteFolder}
                                onCopy={handleCopyFolder}
                            />
                        )}
                    </List>
                    {showCompanies &&
                        <Box>
                            <Divider />
                            {props.companies.map((company) => {
                                if (company.id === props.companyId) return null;
                                return <ListItemButton
                                    key={company.id}
                                    onClick={() => props.onChangeActiveCompany(company.id)}
                                >
                                    <CompanyItem company={company} />
                                </ListItemButton>;
                            })}
                        </Box>
                    }
                    {treeState.isLoading &&
                        <LocalLoading />
                    }
                </MainListContainer>
            </div>
            <DocumentList
                foldersLoading={treeState.isLoading}
                sessionState={props.sessionState}
                canEditProperties={props.canEditProperties}
                companies={props.companies}
                companyId={treeState.companyId}
                filter={listState.filter}
                listFilterIsSet={props.listFilterIsSet}
                selectedKeywordIds={props.selectedKeywordIds}
                selectedKeywordOperatorId={props.selectedKeywordOperatorId}
                selectedDocumentApprovalId={props.selectedDocumentApprovalId}
                selectedDocumentContentSearchId={props.selectedDocumentContentSearchId}
                selectedMaxResultsCount={props.selectedMaxResultsCount}
                selectedDynamicTimeFilterId={props.selectedDynamicTimeFilterId}
                selectedStartDate={props.selectedStartDate}
                selectedEndDate={props.selectedEndDate}
                selectedDynamicDocumentEndDateFilterId={props.selectedDynamicDocumentEndDateFilterId}
                selectedDocumentEndDateStartDate={props.selectedDocumentEndDateStartDate}
                selectedDocumentEndDateEndDate={props.selectedDocumentEndDateEndDate}
                folderTree={treeState.folderTree}
                folderId={treeState.selectedId}
                folderPermission={treeState.permission}
                folderComment={treeState.folderComment}
                //dataId={props.dataId}
                sortColumn={listState.sortColumn}
                sortOrderIsAsc={listState.sortOrderIsAsc}
                showDocumentPreview={props.showDocumentPreview}
                showPublishedVersionOnDocumentPreview={props.showPublishedVersionOnDocumentPreview}
                editMode={props.editMode}
                settingsVisible={props.settingsVisible}
                onSetSortData={handleSetSortData}
                onShowSettingsDialog={props.onShowSettingsDialog}
                onChangeShowDocumentPreview={props.onChangeShowDocumentPreview}
                onChangeShowPublishedVersionOnDocumentPreview={props.onChangeShowPublishedVersionOnDocumentPreview}
                onChangeEditMode={props.onChangeEditMode}
                onChangeFilter={handleChangeFilter}
                onOpenFiltersPanel={props.onOpenFiltersPanel}
            />
            {showCopyMoveDialog &&
                <DocumentCopyMoveDialog
                    sessionState={props.sessionState}
                    companies={props.companies}
                    companyId={treeState.companyId}
                    folderId={treeState.selectedId}
                    folderPermission={treeState.permission}
                    folderTree={treeState.folderTree}
                    items={[]}
                    onOk={handleCopyMoveDialogOk}
                    onCancel={handleCopyMoveDialogCancel}
                />
            }
            {newFolderDialogState.show &&
                <DocumentFolderNewDialog
                    companyId={treeState.companyId}
                    folder={newFolderDialogState.folder}
                    onOk={handleDocumentFolderNewDialogOk}
                    onCancel={handleDocumentFolderNewDialogCancel}
                />
            }
            {editFolderDialogState.show &&
                <DocumentFolderEditDialog
                    sessionState={props.sessionState}
                    isRoot={editFolderDialogState.folder.getIsRoot()}
                    parentFolderPermission={editFolderDialogState.folder.parent?.permission ?? Permission.None}
                    companyId={props.companyId}
                    id={editFolderDialogState.folder.id}
                    hasPrevious={false}
                    hasNext={false}
                    onOk={handleDocumentFolderEditDialogOk}
                    onCancel={handleDocumentFolderEditDialogCancel}
                    onRefreshList={() => loadFolderNode("")}
                    onPrevious={() => {}}
                    onNext={() => {}}
                />
            }
        </Box>
    );
};
